import type { IQuestionLink } from "@agrigate/survey-api-def/lib/SurveysService/dto";
import {
    END,
    GRAPH_FORM_VERSION,
} from "@agrigate/survey-api-def/lib/SurveysService/graph";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import type { SurveyQuestion } from "../SurveyBuilderForm";

export type SurveyLink = IQuestionLink;
export type SurveyFormVersion = 1 | 2;

export { END, GRAPH_FORM_VERSION };

export interface HydratedConfig {
    formVersion: SurveyFormVersion;
    surveyQuestions: SurveyQuestion[];
    links: SurveyLink[];
    entryQuestionId?: string;
    isUnreadable?: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

export const hydrateForm = (rawFormConfig: MaybeN<string>): HydratedConfig => {
    const empty: HydratedConfig = {
        formVersion: 1,
        surveyQuestions: [],
        links: [],
    };
    if (!rawFormConfig) return empty;

    let parsed: unknown;
    try {
        parsed = JSON.parse(rawFormConfig);
    } catch {
        return { ...empty, isUnreadable: true };
    }

    if (Array.isArray(parsed))
        return {
            formVersion: 1,
            surveyQuestions: parsed as SurveyQuestion[],
            links: [],
        };

    if (isRecord(parsed) && parsed.version === GRAPH_FORM_VERSION) {
        const questions = Array.isArray(parsed.questions)
            ? (parsed.questions as SurveyQuestion[])
            : [];
        return {
            formVersion: 2,
            surveyQuestions: questions,
            links: Array.isArray(parsed.links)
                ? (parsed.links as SurveyLink[])
                : [],
            entryQuestionId:
                typeof parsed.entryQuestionId === "string"
                    ? parsed.entryQuestionId
                    : questions[0]?.id,
        };
    }

    return { ...empty, isUnreadable: true };
};

export const serializeFormConfig = (
    formVersion: SurveyFormVersion,
    questions: SurveyQuestion[],
    graph?: { links: SurveyLink[]; entryQuestionId?: MaybeN<string> },
): string => {
    if (formVersion !== 2) return JSON.stringify(questions);
    return JSON.stringify({
        version: GRAPH_FORM_VERSION,
        entryQuestionId: graph?.entryQuestionId ?? questions[0]?.id,
        questions,
        links: graph?.links ?? [],
    });
};

export const linksTouchingQuestion = (
    links: SurveyLink[],
    questionId: string,
) =>
    links.filter(
        link =>
            link.from.questionId === questionId ||
            (link.to !== END && link.to.questionId === questionId),
    );

export const linksPointingAt = (links: SurveyLink[], questionId: string) =>
    links.filter(link => link.to !== END && link.to.questionId === questionId);

export const withoutOption = (
    links: SurveyLink[],
    questionId: string,
    optionIds: string[],
) =>
    links.filter(
        link =>
            !(
                link.from.questionId === questionId &&
                link.from.optionId != null &&
                optionIds.includes(link.from.optionId)
            ),
    );
